<script>
	import { onMount, onDestroy, tick } from 'svelte';
	import { writable } from 'svelte/store';
	import WavingHand from './components/WavingHand.svelte';
	import QuestionModal from './components/QuestionModal.svelte';
	import AnswerPlayer from './components/AnswerPlayer.svelte';
	import SocialSharing from './components/SocialSharing.svelte';
  	import FinalAudioPlayer from './components/FinalAudioPlayer.svelte';
	import AudioLoader from './components/AudioLoader.svelte';

	const stages = {
		TITLE: 'title',
		INTRO: 'intro',
		MAIN: 'main'
	};
	
	const currentStage = writable(stages.TITLE);
	let scrollLocked = true;
	const wsProtocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';

  
	const BACKEND_URL = window.location.hostname === 'localhost' ? 'http://localhost:5000' : '';
	let socket;
  
	let essay = [];
	let currentSegment = 0;
	let isPlaying = false;
	let isHandRaised = false;
	let isQuestionModalOpen = false;
	let question = '';
	let isWaitingForAudio = false;
	let isWaitingForAnswer = false;
	let errorMessage = '';
	let isRecording = false;
	let mediaRecorder;
	let audioChunks = [];
	let playedAudioPaths = [];
	let commentSegmentId = null;
	let comments = {};
	let showWavingHand = false;
	let showControls = false;
	let questionHistory = [];
	let playingAIAnswer = false;
	let finalAudioRequested = false;
	let currentAudioContext = null;
	let isStitchingAudio = false;
  
	let audioElement;
	let bufferAudioElement;
	let aiAudioElement;
	let recordingAudioContext = null;

	let introSegment = null;	
	let essayStarted = false;
	let pendingInteractions = [];
	let socketReady = false;
	let audioCache = new Map();
	let preloadComplete = false;
	let loadingProgress = 0;
	let collapsedComments = new Set();
	let menuVisible = false;
	let aiMode = 'sonnet'; // Default to more realistic
	let titleOpacity = 0;
	let isMobile = isMobileDevice();
	const desktopInfoText = "This is a <b>human-created interactive audio essay</b>: it's really me talking, but sometimes it's not. Like reading ahead? As you read, <b>hover over any passage and click on the 💬 that appears to its right</b> to leave a comment or ask a question, and the audio essay will dynamically update to address what you wrote. If you've got a question in the moment, <b> click the hand (✋) in the bottom-right controls </b> to raise your hand. An AI moderator will choose the next best time to take your question without disrupting the overall experience. When the essay is finished, you can download your own unique version of the audio essay, complete with your interactions with AI-me.";
  	const mobileInfoText = "This is a <b>human-created interactive audio essay</b>: it's really me talking, but sometimes it's not. If you've got a question in the moment, <b> click the hand (✋) in the bottom-right controls </b> to raise your hand. An AI moderator will choose the next best time to take your question without disrupting the overall experience. When the essay is finished, you can download your own unique version of the audio essay, complete with your interactions with AI-me. (and check out the desktop version for more ways to interact with...me?)";

	async function resetAudioContext() {
		if (currentAudioContext) {
			try {
				await currentAudioContext.close();
			} catch (e) {
				console.error('Error closing audio context:', e);
			}
			currentAudioContext = null;
		}
	}

	// Helper to ensure we have a fresh audio context
	async function ensureFreshAudioContext() {
		await resetAudioContext();
		currentAudioContext = new (window.AudioContext || window.webkitAudioContext)();
		await currentAudioContext.resume();
		return currentAudioContext;
	}

	function isMobileDevice() {
		return /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent) 
			|| (navigator.maxTouchPoints && navigator.maxTouchPoints > 2);
	}

	function findNextAllowedSegment(currentIndex, permission) {
		for (let i = currentIndex + 1; i < essay.length; i++) {
			if (permission === 'raise_hand_allowed' && essay[i].raise_hand_allowed) {
				return essay[i];
			}
			if (permission === 'comment_updates_segment' && essay[i].comment_updates_segment) {
				return essay[i];
			}
		}
		return null;
	}

	function resumeEssay() {
		isPlaying = true;
		audioElement.play().catch(error => {
			console.error('Error resuming audio:', error);
			isPlaying = false;
		});
	}
  
	onMount(async () => {
	  currentStage.set(stages.TITLE);
	// Lock scroll for title view
	  lockScroll();
	  resetApp();
	  audioElement = new Audio();
	  audioElement.crossOrigin = "anonymous";
	  await fetchEssay();
	  await preloadAudio();

	  audioElement.addEventListener('pause', () => {
			// Check if this is:
			// 1. Not the end of a segment (ended property)
			// 2. Not our own programmatic pause (isPlaying should be true)
			// 3. Not in the middle of transitioning between segments (can check currentTime)
			if (isPlaying && !audioElement.ended && audioElement.currentTime > 0) {
				console.log('External pause detected (likely system/hardware pause)');
				isPlaying = false;
			}
		});

		audioElement.addEventListener('play', () => {
			// Only update state if this wasn't triggered by our resumeEssay function
			// and we're not in the middle of segment transition
			if (!isPlaying && audioElement.currentTime > 0) {
				console.log('External play detected (likely system/hardware resume)');
				isPlaying = true;
			}
		});

	  bufferAudioElement = new Audio();
	  bufferAudioElement.crossOrigin = "anonymous";
  
	  socket = new WebSocket(window.location.hostname === 'localhost' 
		? 'ws://localhost:5000/ws'
		: `${wsProtocol}//${window.location.host}/ws`);

	  socket.onopen = () => {
        console.log('WebSocket connected');
        socketReady = true;
    	};

		socket.onclose = () => {
			console.log('WebSocket disconnected');
			socketReady = false;
		};

		socket.onmessage = (event) => {
			const data = JSON.parse(event.data);
			switch (data.type) {
				case 'audio_update':
					handleAudioUpdate(data);
					break;
				case 'audio_ready':
					handleAudioReady(data);
					break;
				case 'play_answer':
					handlePlayAnswer(data);
					break;
				case 'comment_error':
					handleCommentError(data);
					break;
				case 'answer_error':
					handleAnswerError(data);
					break;
				case 'final_audio_ready':
					handleFinalAudioReady(data);
					break;
				case 'segment_update_started':
					handleSegmentUpdateStarted(data);
					break;
				case 'segment_update_ready':
					handleSegmentUpdateReady(data);
					break;
				case 'comment_error':
					handleCommentError(data);
					break;
				case 'written_comment_ready':
					handleWrittenCommentReady(data);
					break;
			}
		};
  
		bufferAudioElement.src = `${BACKEND_URL}/audio/buffers/buffer1.mp3`;
		bufferAudioElement.load();
		audioElement.onerror = (e) => {
			console.error('Audio element error:', e);
			if (currentSegment < essay.length) {
			handleSegmentCompletion(essay[currentSegment], currentSegment);
				}
			};
		
		const handleScroll = () => {
			if ($currentStage === stages.MAIN) {
				const scrollPosition = window.scrollY;
				const triggerPoint = 300; // Adjust this value to control when title starts appearing
				const fadeDistance = 200; // How many pixels to complete the fade

				if (scrollPosition > triggerPoint) {
					titleOpacity = Math.min((scrollPosition - triggerPoint) / fadeDistance, 1);
					menuVisible = true;
				} else {
					titleOpacity = 0;
					menuVisible = false;
				}
			}
    };

    window.addEventListener('scroll', handleScroll);
    handleScroll(); // Initial call
    return () => window.removeEventListener('scroll', handleScroll);
	});
  
	function resetApp() {
	  currentSegment = 0;
	  isPlaying = false;
	  isHandRaised = false;
	  isQuestionModalOpen = false;
	  question = '';
	  isWaitingForAudio = false;
	  isWaitingForAnswer = false;
	  errorMessage = '';
	  isRecording = false;
	  audioChunks = [];
	  playedAudioPaths = [];
	  commentSegmentId = null;
	  comments = {};
	  showWavingHand = false;
	  showControls = false;
	  essayStarted = false;
	}

	async function preloadAudio() {
		try {

			if (isMobile) {
			console.log('Mobile device detected, skipping audio preload');
			loadingProgress = 100;
			preloadComplete = true;
			return;
			}

			const audioPaths = essay
			.filter(segment => segment.play_live)
			.map(segment => segment.play_path);
			
			const uiSounds = [
			'nod_to_user.mp3',
			'breakfromessay.mp3',
			'backtoessay.mp3',
			'buffers/buffer1.mp3'
			];
			audioPaths.push(...uiSounds);
			
			try {
			const nodsResponse = await fetch(`${BACKEND_URL}/api/list_nods`, { credentials: 'include' });
			if (nodsResponse.ok) {
				const nods = await nodsResponse.json();
				audioPaths.push(...nods.map(nod => `nods/${nod}`));
			}
			} catch (error) {
			console.warn("Couldn't fetch nod variations, will use default:", error);
			}
			
			let loaded = 0;
			await Promise.all(audioPaths.map(async path => {
			try {
				// Use the same path handling logic as playSegmentAudio
				const fetchPath = path.startsWith('audio/') 
				? path.replace(/^audio\//, '')
				: path;
				
				console.log(`Attempting to fetch: ${fetchPath} (original: ${path})`);
				
				const response = await fetch(`${BACKEND_URL}/audio/${fetchPath}`, {
				credentials: 'include'
				});
				
				if (!response.ok) {
				console.error(`Failed to fetch ${fetchPath} (${response.status})`);
				throw new Error(`HTTP error! status: ${response.status}`);
				}
				
				const blob = await response.blob();
				const url = URL.createObjectURL(blob);
				
				const tempAudio = new Audio();
				await new Promise((resolve, reject) => {
				tempAudio.onloadeddata = resolve;
				tempAudio.onerror = reject;
				tempAudio.src = url;
				});
				
				audioCache.set(path, url);
				
				loaded++;
				loadingProgress = (loaded / audioPaths.length) * 100;
				console.log(`Successfully loaded: ${path} -> ${fetchPath}`);
			} catch (error) {
				console.error(`Failed to preload audio: ${path}`, error);
			}
			}));
			
			preloadComplete = true;
			console.log('Audio preload complete, cache contents:', Array.from(audioCache.keys()));
		} catch (error) {
			console.error('Error during audio preload:', error);
			preloadComplete = true;
		}
	}

	function handleSegmentUpdateStarted(data) {
		essay = essay.map(segment => 
			segment.segment === data.segment_id 
			? {...segment, update_flag: true, ai_content_ready: false} 
			: segment
		);
		
		if (data.segment_id === essay[currentSegment]?.segment && isPlaying) {
			isWaitingForAudio = true;
			playBufferAudio();
		}
	}

	function lockScroll() {
		scrollLocked = true;
		document.body.style.overflow = 'hidden';
	}

	function unlockScroll() {
		scrollLocked = false;
		document.body.style.overflow = 'auto';
	}
  
	async function startAndScroll() {
		essayStarted = true;
		unlockScroll();
		
		// Smooth scroll to intro
		const introSection = document.querySelector('.intro-view');
		introSection.scrollIntoView({ behavior: 'smooth' });
		
		// Wait for scroll to complete
		await new Promise(resolve => setTimeout(resolve, 1000)); // Adjust timing as needed
		
		currentStage.set(stages.INTRO);
		lockScroll();
		playIntroSegment();
	}

	function playIntroSegment() {
		if (!essay.length) return;
		introSegment = essay.find(segment => segment.segment === 1);
		if (introSegment) {
		isPlaying = true;
		currentSegment = 0;
		playSegment(currentSegment);
		}
	}

	async function skipIntro() {
		if (audioElement) {
		audioElement.pause();
		}
		
		unlockScroll();
		
		// Smooth scroll to main content
		const mainContent = document.querySelector('.essay-content');
		mainContent.scrollIntoView({ behavior: 'smooth' });
		
		// Wait for scroll to complete
		await new Promise(resolve => setTimeout(resolve, 1000)); // Adjust timing as needed
		
		currentStage.set(stages.MAIN);
		// Find first non-intro segment
		currentSegment = essay.findIndex(segment => segment.segment !== 1);
		if (currentSegment === -1) currentSegment = 0;
		isPlaying = true;
		playSegment(currentSegment);
	}

	function handleIntroComplete() {
		skipIntro();
	}
  
	async function fetchEssay() {
	try {
		const response = await fetch(`${BACKEND_URL}/api/essay`, { credentials: 'include' });
		if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);
		essay = await response.json();
		console.log('Fetched essay data:', essay);
	} catch (error) {
		console.error("Failed to fetch essay:", error);
		errorMessage = `Failed to fetch essay: ${error.message}`;
	}
	}

	function startEssay() {
		if (essay.length > 0) {
			isPlaying = true;
			currentSegment = essay.findIndex(segment => segment.play_live);
			if (currentSegment === -1) {
				console.error("No playable segments found");
				return;
			}
			playedAudioPaths = [];
			playSegment(currentSegment);
		}
	}
  
	function pauseEssay() {
		isPlaying = false;
		audioElement.pause();
	}
  
	function playSegment(index) {
		if ($currentStage === stages.INTRO && index > 0) {
			skipIntro();
			return;
		}

		// Find the next segment that should be played
		let playIndex = index;
		while (playIndex < essay.length && !essay[playIndex].play_live) {
			playIndex++;
		}

		console.log('Current playIndex:', playIndex);
		console.log('Any remaining playable segments:', essay.slice(playIndex).some(segment => segment.play_live));

		// Check if this is segment 44 - trigger final audio but continue playing
		if (!finalAudioRequested && essay[playIndex]?.segment === 44) {
			console.log('Segment 44 started, requesting final audio compilation...');
			finalAudioRequested = true;
			requestFinalAudio();
		}

		// Original end-of-essay check as fallback
		if (!finalAudioRequested && (playIndex >= essay.length || !essay.slice(playIndex).some(segment => segment.play_live))) {
			console.log('No more playable segments, requesting final audio...');
			isPlaying = false;
			finalAudioRequested = true;
			requestFinalAudio();
			return;
		}

		// Reset all segments' playing state
		essay = essay.map(segment => ({
			...segment,
			is_playing: false
		}));

		// Set current segment as playing
		essay[playIndex].is_playing = true;
		currentSegment = playIndex;

		const segment = essay[playIndex];
		
		// Check if this is a target segment for a pending question
		const pendingQuestion = pendingInteractions.find(
			pi => pi.type === 'question' && pi.targetSegmentId === segment.segment
		);

		if (pendingQuestion) {
			// If this is the target segment, play it and then handle the question
			playSegmentAudio(segment, playIndex, false, () => {
			playNodToUser();
			// Remove this pending interaction
			pendingInteractions = pendingInteractions.filter(pi => pi !== pendingQuestion);
			});
		} else {
			// Normal playback
			if (segment.update_flag) {
			if (segment.ai_content_ready) {
				playSegmentAudio(segment, playIndex, true);
			} else {
				isWaitingForAudio = true;
				playBufferAudio();
			}
			} else {
			playSegmentAudio(segment, playIndex, false);
			}
		}
	}
  
	async function playSegmentAudio(segment, index, useAiAudio, onComplete) {
		let audioPath = useAiAudio ? segment.hybrid_audio_path : segment.play_path;
		if (!audioPath) {
			console.error(`No audio path found for segment ${segment.segment}`);
			return;
		}

		try {
			currentSegment = index;
			console.log(`Playing segment ${segment.segment}, path: ${audioPath}`);
			
			if (isMobileDevice()) {
			// On mobile, always load directly
			const fetchPath = audioPath.startsWith('audio/') 
				? audioPath.replace(/^audio\//, '')
				: audioPath;
			audioElement.src = `${BACKEND_URL}/audio/${fetchPath}`;
			} else {
			// On desktop, try cache first
			if (audioCache.has(audioPath)) {
				console.log(`Using cached audio for ${audioPath}`);
				audioElement.src = audioCache.get(audioPath);
			} else {
				const fetchPath = audioPath.startsWith('audio/') 
				? audioPath.replace(/^audio\//, '')
				: audioPath;
				audioElement.src = `${BACKEND_URL}/audio/${fetchPath}`;
			}
			}
			
			await audioElement.play();
			playedAudioPaths.push(audioPath);
			
			audioElement.onended = () => {
			if (onComplete) {
				onComplete();
			} else {
				handleSegmentCompletion(segment, index);
			}
			};
		} catch (error) {
			console.error('Error playing audio:', error);
			handleSegmentCompletion(segment, index);
		}
		}

	function handleSegmentCompletion(segment, index, pendingQuestion = null) {
		console.log('handleSegmentCompletion called for segment:', segment.segment);
		segment.is_playing = false;
		segment.is_finished = true;
		
		if (pendingQuestion) {
			console.log('Handling pending question');
			playNodToUser();
			// Remove this pending interaction
			pendingInteractions = pendingInteractions.filter(pi => pi !== pendingQuestion);
		} else {
			console.log('Moving to next segment from index:', index);
			playNextSegment(index);
		}
	}
  
	function handleAudioUpdate(data) {
	  essay = essay.map(segment =>
		segment.segment === data.segment_id
		  ? { ...segment, update: true, ai_audio_ready: false }
		  : segment
	  );
  
	  if (data.segment_id === essay[currentSegment]?.segment) {
		isWaitingForAudio = true;
		playBufferAudio();
	  }
	}
  
	function handleAudioReady(data) {
	  essay = essay.map(segment =>
		segment.segment === data.segment_id
		  ? {
			  ...segment,
			  audio_path: data.play_path,
			  ai_audio_ready: true,
			  update: false
			}
		  : segment
	  );
  
	  if (isWaitingForAudio && data.segment_id === essay[currentSegment]?.segment) {
		isWaitingForAudio = false;
		stopBufferAudio();
		playSegment(currentSegment);
	  }
	}
  
	function handleRaiseHand() {
		isHandRaised = true;
		showWavingHand = true;
		
		// Find currently playing segment
		const playingSegment = essay.find(s => s.is_playing);
		if (!playingSegment) return;
		
		const currentPlayingIndex = essay.indexOf(playingSegment);
		console.log('Raising hand during segment:', playingSegment.segment);
		console.log('Current segment index:', currentPlayingIndex);

		// If audio is currently playing, wait for it to finish
		if (!audioElement.paused) {
			audioElement.onended = () => {
			if (playingSegment.raise_hand_allowed) {
				// If current segment allows questions, handle it immediately
				playNodToUser();
			} else {
				// Find next segment that allows questions
				const targetSegment = findNextAllowedSegment(currentPlayingIndex, 'raise_hand_allowed');
				if (targetSegment) {
				// Create pending interaction
				pendingInteractions.push({
					type: 'question',
					content: null,
					originalSegmentId: playingSegment.segment,
					targetSegmentId: targetSegment.segment,
					originalText: playingSegment.text_content,
					targetText: targetSegment.text_content
				});
				
				// Continue playing from next segment
				currentSegment = currentPlayingIndex + 1;
				playSegment(currentSegment);
				} else {
				errorMessage = "Questions aren't allowed at this point in the essay.";
				showWavingHand = false;
				isHandRaised = false;
				// Continue playing from next segment
				playSegment(currentPlayingIndex + 1);
				}
			}
			};
		}
		}

	// Helper function to handle segment progression
	function playNextSegment(currentIndex) {
		console.log('playNextSegment called with index:', currentIndex);
    	console.log('Current isPlaying state:', isPlaying);
		console.log('Playing segment with index:', currentIndex);
		currentSegment = currentIndex + 1;
		console.log('Next segment index:', currentSegment);
		
		// Check if we've reached the end
		if (currentSegment >= essay.length || !essay[currentSegment]) {
			console.log('Reached end of essay');
			isPlaying = false;
			if (!finalAudioRequested) {
			finalAudioRequested = true;
			requestFinalAudio();
			}
			return;
		}
		
		if (isPlaying) {
			console.log('Playing next segment:', essay[currentSegment].segment);
			playSegment(currentSegment);
		}
	}
  
	async function playNodToUser() {
		console.log('Playing nod to user');
		try {
			// Get random nod audio path
			const response = await fetch(`${BACKEND_URL}/api/random_nod`, {
				credentials: 'include'
			});
			if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);
			const data = await response.json();
			
			audioElement.src = `${BACKEND_URL}/audio/${data.audio_path}`;
			showWavingHand = false;
			isHandRaised = false;
			
			audioElement.play().catch(error => console.error('Error playing nod audio:', error));
			
			audioElement.onended = () => {
				console.log('Nod audio ended, opening question modal');
				isQuestionModalOpen = true;
			};
		} catch (error) {
			console.error("Error getting random nod:", error);
			// Fallback to default nod
			audioElement.src = `${BACKEND_URL}/audio/nod_to_user.mp3`;
			audioElement.play();
		}
	}

	function cancelQuestion() {
		isQuestionModalOpen = false;
		isHandRaised = false;
		currentSegment++;
		if (currentSegment < essay.length) {
		playSegment(currentSegment);
		} else {
		isPlaying = false;
		requestFinalAudio();
		}
	}
  
	function handleQuestionSubmit(audioBlob = null) {
		isQuestionModalOpen = false;
		isWaitingForAnswer = true;
		console.log('Starting handleQuestionSubmit, audio blob present:', !!audioBlob);
		
		// Start buffer audio immediately and log its state
		if (bufferAudioElement) {
			bufferAudioElement.currentTime = 0;
			console.log('Playing buffer audio...');
			bufferAudioElement.play().catch(error => {
			console.error('Error playing buffer audio:', error);
			// Don't set isWaitingForAudio to false here - we still want to wait for the answer
			});
		}

		try {
			const formData = new FormData();
			
			if (audioBlob) {
			formData.append('audio', audioBlob, 'recording.wav');
			}

			// Add question data
			const questionData = {
			segment_id: essay[currentSegment].segment,
			question: audioBlob ? '' : question
			};

			formData.append('data', JSON.stringify(questionData));

			// Send to backend and log states
			fetch(`${BACKEND_URL}/api/raise_hand`, {
			method: 'POST',
			body: formData,
			credentials: 'include'
			}).then(response => {
			if (!response.ok) {
				throw new Error(`HTTP error! status: ${response.status}`);
			}
			console.log('Question submitted successfully');
			}).catch(error => {
			console.error("Failed to submit question:", error);
			errorMessage = `Failed to submit question: ${error.message}`;
			isWaitingForAnswer = false;
			stopBufferAudio();
			});

		} catch (error) {
			console.error("Failed to submit question:", error);
			errorMessage = `Failed to submit question: ${error.message}`;
			isWaitingForAnswer = false;
			stopBufferAudio();
		}
		
		question = '';
		}
  
		function handlePlayAnswer(data) {
			console.log('Starting handlePlayAnswer');
			isWaitingForAnswer = false;
			stopBufferAudio();
			playingAIAnswer = true;
			
			const audioPath = data.audio_path.replace(/^audio\//, '');
			console.log(`Setting up AI answer audio from: ${BACKEND_URL}/audio/${audioPath}`);
			
			// Use dedicated AI audio element
			aiAudioElement.src = `${BACKEND_URL}/audio/${audioPath}`;
			
			const playAttempt = aiAudioElement.play();
			if (playAttempt !== undefined) {
				playAttempt.then(() => {
					console.log('AI answer playback started successfully');
				}).catch(error => {
					console.error('Error playing AI answer audio:', error);
					playingAIAnswer = false;
				});
			}

			playedAudioPaths.push(data.audio_path);

			aiAudioElement.onended = () => {
				console.log('AI answer ended, setting up transition');
				playingAIAnswer = false;
				
				// Play transition on main audio element
				audioElement.src = `${BACKEND_URL}/audio/backtoessay.mp3`;
				audioElement.play().catch(error => {
					console.error('Error playing transition audio:', error);
					proceedToNextSegment();
				});
				
				audioElement.onended = proceedToNextSegment;
			};
		}

	// Helper function to try alternative playback method
	async function tryAlternativePlayback(audioPath) {
		console.log('Trying alternative playback method');
		try {
			const response = await fetch(`${BACKEND_URL}/audio/${audioPath}`);
			const audioData = await response.arrayBuffer();
			
			const audioContext = new (window.AudioContext || window.webkitAudioContext)();
			const audioBuffer = await audioContext.decodeAudioData(audioData);
			
			const source = audioContext.createBufferSource();
			source.buffer = audioBuffer;
			source.connect(audioContext.destination);
			source.start(0);
			
			source.onended = () => {
			console.log('Alternative playback ended');
			proceedToNextSegment();
			};
		} catch (error) {
			console.error('Alternative playback failed:', error);
			proceedToNextSegment();
		}
	}

	// Helper function to handle segment progression
	function proceedToNextSegment() {
		console.log('Proceeding to next segment');
		isHandRaised = false;
		isPlaying = true;
		currentSegment++;
		if (currentSegment < essay.length) {
			playSegment(currentSegment);
		}
	}
  
	function handleAnswerError(data) {
	  errorMessage = `Error generating answer: ${data.error}`;
	  isWaitingForAnswer = false;
	  stopBufferAudio();
	}
  
	async function playBufferAudio() {
		if (isMobile) {
			await ensureFreshAudioContext();
			
			// Explicitly connect buffer audio element to new context
			const source = currentAudioContext.createMediaElementSource(bufferAudioElement);
			source.connect(currentAudioContext.destination);
		}
		
		bufferAudioElement.currentTime = 0;
		bufferAudioElement.play().catch(error => {
			console.error('Error playing buffer audio:', error);
			isWaitingForAudio = false;
		});
	}
  
	function stopBufferAudio() {
	  bufferAudioElement.pause();
	  bufferAudioElement.currentTime = 0;
	}
  
	async function startRecording() {
		try {
			const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
			mediaRecorder = new MediaRecorder(stream);
			audioChunks = [];
			
			// Store context if on mobile
			if (isMobile) {
				recordingAudioContext = new (window.AudioContext || window.webkitAudioContext)();
			}

			mediaRecorder.ondataavailable = (event) => {
				if (event.data.size > 0) {
					audioChunks.push(event.data);
				}
			};

			mediaRecorder.start(100);
			isRecording = true;
			console.log('Recording started');

		} catch (error) {
			console.error("Error accessing microphone:", error);
			errorMessage = `Error accessing microphone: ${error.message}`;
			isRecording = false;
		}
	}

	async function cleanupRecordingAudio() {
		if (!isMobile) return;

		console.log('Cleaning up mobile recording audio...');
		
		// Stop all tracks
		if (mediaRecorder && mediaRecorder.stream) {
			mediaRecorder.stream.getTracks().forEach(track => {
				console.log('Stopping track:', track.kind);
				track.stop();
			});
		}

		// Close recording context if it exists
		if (recordingAudioContext) {
			try {
				console.log('Closing recording audio context...');
				await recordingAudioContext.close();
				recordingAudioContext = null;
			} catch (e) {
				console.error('Error closing recording context:', e);
			}
		}

		// Brief pause to let cleanup complete
		await new Promise(resolve => setTimeout(resolve, 100));
		console.log('Mobile recording cleanup complete');
	}

	function stopRecording() {
		console.log('Stopping recording...');
		if (mediaRecorder && mediaRecorder.state === "recording") {
			isQuestionModalOpen = false;
			isWaitingForAnswer = true;
			
			mediaRecorder.stop();
			isRecording = false;
			
			mediaRecorder.onstop = async () => {
				console.log('MediaRecorder stopped, starting cleanup...');
				
				// Do mobile cleanup first
				await cleanupRecordingAudio();
				
				const audioBlob = new Blob(audioChunks, { type: 'audio/wav' });
				console.log('Audio blob created, submitting question...');
				
				// Now submit the question
				handleQuestionSubmit(audioBlob);
			};
		}
	}
  
	async function requestFinalAudio() {
		console.log('Requesting final audio...');
		isStitchingAudio = true; // Show loader
		
		if (!socketReady) {
		console.error('WebSocket not connected, cannot request final audio');
		isStitchingAudio = false; // Hide loader if there's an error
		return;
		}

		try {
		const message = { 
			type: 'request_final_audio', 
			playedAudioPaths,
			questionHistory: questionHistory || [],
			essay
		};
		
		socket.send(JSON.stringify(message));
		} catch (error) {
		console.error('Error requesting final audio:', error);
		isStitchingAudio = false; // Hide loader if there's an error
		}
	}
  
	function handleFinalAudioReady(data) {
		finalAudioLink = `${BACKEND_URL}/${data.finalAudioPath}`;
		showFinalAudioLink = true;
		isStitchingAudio = false; // Hide loader once audio is ready
	}
  
	let finalAudioLink = '';
	let showFinalAudioLink = false;
	let isCommentModalOpen = false;
	let comment = '';

	function openCommentBox(segmentId) {
		if (segmentId < currentSegment) {
			// Disable commenting on already played segments
			return;
		}
		commentSegmentId = segmentId;
		isCommentModalOpen = true;
		comment = '';
	}

	function closeCommentBox() {
		commentSegmentId = null;
		isCommentModalOpen = false;
	}

	async function submitComment(segmentId, commentText) {
		if (commentText) {
			try {
				const segment = essay.find(s => s.segment === segmentId);
				
				if (segment.comment_updates_segment) {
					const response = await fetch(`${BACKEND_URL}/api/comment`, {
						method: 'POST',
						headers: { 'Content-Type': 'application/json' },
						body: JSON.stringify({ 
							segment_id: segmentId, 
							comment: commentText 
						}),
						credentials: 'include'
					});
					
					if (!response.ok) {
						const errorData = await response.json();
						throw new Error(errorData.error || `HTTP error! status: ${response.status}`);
					}
				} else {
					const targetSegment = findNextAllowedSegment(
						essay.findIndex(s => s.segment === segmentId),
						'comment_updates_segment'
					);
					
					if (targetSegment) {
						const response = await fetch(`${BACKEND_URL}/api/comment`, {
							method: 'POST',
							headers: { 'Content-Type': 'application/json' },
							body: JSON.stringify({
								segment_id: segmentId,
								target_segment_id: targetSegment.segment,
								comment: commentText,
								original_text: segment.text_content,
								target_text: targetSegment.text_content
							}),
							credentials: 'include'
						});
						
						if (!response.ok) {
							const errorData = await response.json();
							throw new Error(errorData.error || `HTTP error! status: ${response.status}`);
						}
					}
				}
				
				// Update the comments state immediately with the user's comment
				comments[segmentId] = {
					text: commentText,
					aiReply: null  // Will be filled in when the reply comes back
				};
      			comments = { ...comments }; // Trigger Svelte reactivity
				closeCommentBox();
			} catch (error) {
				console.error("Failed to submit comment:", error);
				errorMessage = `Failed to submit comment: ${error.message}`;
			}
		}
	}

	function handleWrittenCommentReady(data) {
		const { segment_id, comment: aiReply } = data;
		if (comments[segment_id]) {
			comments[segment_id] = {
			...comments[segment_id],
			aiReply
			};
			comments = { ...comments }; // Trigger Svelte reactivity
		}
	}

	function handleSegmentUpdateReady(data) {
		essay = essay.map(segment => 
			segment.segment === data.segment_id 
			? {
				...segment, 
				hybrid_audio_path: data.new_audio_path,
				play_path: data.new_audio_path,
				ai_content_ready: true,
				update_flag: true  // Make sure update_flag stays true
			} 
			: segment
		);
		
		if (data.segment_id === essay[currentSegment]?.segment && isWaitingForAudio) {
			isWaitingForAudio = false;
			stopBufferAudio();
			playSegment(currentSegment);
		}
	}

	function handleCommentError(data) {
		console.error("Error processing comment:", data.error);
		errorMessage = `Error processing comment: ${data.error}`;
		// Reset the update flags for the segment
		essay = essay.map(segment => 
			segment.segment === data.segment_id 
			? {...segment, update: false, ai_content_ready: false} 
			: segment
		);
	}

	function restartEssay() {
		// Stop any playing audio
		if (audioElement) {
			audioElement.pause();
			audioElement.currentTime = 0;
		}
		if (bufferAudioElement) {
			bufferAudioElement.pause();
			bufferAudioElement.currentTime = 0;
		}
		
		// Reset app state
		resetApp();
		currentStage.set(stages.TITLE);
		
		// Unlock scroll for transition
		unlockScroll();
		
		// Scroll back to top
		window.scrollTo({
			top: 0,
			behavior: 'smooth'
		});
		
		// Lock scroll after transition
		setTimeout(() => {
			lockScroll();
		}, 1000);
	}

	onDestroy(() => {
		audioCache.forEach(url => {
			URL.revokeObjectURL(url);
		});
	});

	function toggleComment(segmentId) {
		if (collapsedComments.has(segmentId)) {
			collapsedComments.delete(segmentId);
		} else {
			collapsedComments.add(segmentId);
		}
		collapsedComments = collapsedComments; // Trigger reactivity
	}

	async function toggleAIMode() {
		aiMode = aiMode === 'sonnet' ? 'haiku' : 'sonnet';
		
		try {
			// Notify backend of AI mode change
			const response = await fetch(`${BACKEND_URL}/api/set_ai_mode`, {
				method: 'POST',
				headers: {
					'Content-Type': 'application/json',
				},
				body: JSON.stringify({ mode: aiMode }),
				credentials: 'include'
			});
			
			if (!response.ok) {
				console.error('Failed to update AI mode');
			}
		} catch (error) {
			console.error('Error updating AI mode:', error);
		}
	}

	function handleRestart() {
		// Reset final audio player state
		finalAudioRequested = false;
		showFinalAudioLink = false;
		finalAudioLink = '';
		
		// Call existing restart function
		restartEssay();
	}
</script>

<main>
	{#if !preloadComplete}
	<div class="loading-overlay">
		<div class="loading-content">
		<h2>Loading Essay Audio...</h2>
		<div class="progress-bar">
			<div class="progress" style="width: {loadingProgress}%"></div>
		</div>
		<p>{Math.round(loadingProgress)}%</p>
		</div>
	</div>
	{/if}
	<div class="content-container">
	  <section class="title-view" class:hidden={$currentStage === stages.MAIN}>
		<h1>Sentient AI is Closer Than You Think. We're Not Ready.</h1>
		<p>(well, sort of. let me explain.)</p>
		<button class="start-button" on:click={startAndScroll}>
		  <span><b>Start</b></span>
		  <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
			<path d="M12 5v14M5 12l7 7 7-7"/>
		  </svg>
		</button>
	  </section>
  
	  <section class="intro-view" class:hidden={$currentStage === stages.MAIN}>
		{#if introSegment}
		  <div class="intro-content">
			{@html introSegment.text_content}
		  </div>
		  <button class="skip-button" on:click={skipIntro}>
			<span><b>(skip intro)</b></span>
			<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
			  <path d="M12 5v14M5 12l7 7 7-7"/>
			</svg>
		  </button>
		{/if}
	  </section>
  
	  {#if $currentStage !== stages.TITLE}
		<section class="main-content">
		<!-- Menu Bar -->
		<div class="menu-bar {menuVisible ? 'visible' : ''}">
			<div class="menu-left">
				<button class="menu-button" on:click={handleRestart}>Reset</button>
			</div>
			{#if !isMobile}
				<div class="menu-center">
				<h2 class="menu-bar-title" style="opacity: {titleOpacity}">
					Sentient AI is Closer Than You Think. We're Not Ready.
				</h2>
				</div>
			{/if}
			<div class="menu-right">
				<a href="https://actually-useful.xyz/about-essay" target="_blank" class="menu-button">About</a>
			</div>
		</div>
		  {#if $currentStage === stages.MAIN}
			<!-- Desktop ai toggle -->
			{#if !isMobile}
				<div class="floating-controls-container">
					<span class="floating-controls-label"><b><i>ai model should be</i></b></span>
					<div class="floating-ai-toggle">
						<span class="floating-toggle-option {aiMode === 'haiku' ? 'active' : ''}"
							on:click={toggleAIMode}
							data-tooltip="haiku (anthropic)">
							faster
						</span>
						<span class="floating-toggle-option {aiMode === 'sonnet' ? 'active' : ''}"
							on:click={toggleAIMode}
							data-tooltip="sonnet (anthropic)">
							more realistic
						</span>
					</div>
				</div>
			{/if}
		  {/if}
		  <article class="essay-content">
			<h1>Sentient AI is Closer Than You Think. We're Not Ready.</h1>
			<h3>Agasthya Shenoy</h3>
			<p id='info'>{@html isMobile ? mobileInfoText : desktopInfoText}</p>
			<div class="gif-container">
				<video 
					autoplay 
					loop 
					muted 
					playsinline 
					class="essay-gif"
				>
					<source src="/intro_with_alpha.webm" type="video/webm">
				</video>
			</div>
			{#each essay as segment, index}
			  {#if segment.display_to_user && segment.segment !== 1}
				<div 
				  class="segment-container"
				  class:active={index === currentSegment}
				  class:played={index < currentSegment}
				>
				  {@html segment.text_content}
				  {#if !isMobile && index >= currentSegment}
					<div class="comment-tooltip" on:click={() => openCommentBox(segment.segment)}>
					  💬
					</div>
				  {/if}
				  {#if !isMobile && commentSegmentId === segment.segment && isCommentModalOpen}
					<div class="comment-box">
					  <textarea bind:value={comment} placeholder="Add a comment..."></textarea>
					  <button on:click={() => submitComment(segment.segment, comment)}>Submit</button>
					  <button on:click={closeCommentBox}>Cancel</button>
					</div>
				  {/if}
				  {#if !isMobile && comments[segment.segment]}
						<div class="submitted-comment {collapsedComments.has(segment.segment) ? 'collapsed' : ''}"
							on:click={() => toggleComment(segment.segment)}>
							<div class="comment-header">
								<span class="collapse-icon"> &#8595; </span>
								<span>{collapsedComments.has(segment.segment) ? '  view comment' : '  collapse comment'}</span>
							</div>
							<div class="comment-content">
								<div class="user-comment">
									<div class="comment-avatar">👤</div>
									<p>{comments[segment.segment].text}</p>
								</div>
								{#if comments[segment.segment].aiReply}
									<div class="ai-reply">
										<div class="reply-avatar">🤖</div>
										<p>{comments[segment.segment].aiReply}</p>
									</div>
								{/if}
							</div>
						</div>
					{/if}
				</div>
			  {/if}
			{/each}
			{#if showFinalAudioLink}
				<div>
					<FinalAudioPlayer audioUrl={finalAudioLink} />
				</div>
			{/if}
			<hr>
		  <p>This experiment was coded, produced, and scored by Agasthya Shenoy.</p><p>Agasthya is currently an AI Fellow at <a href="https://www.teachinglabstudio.com/" target="_blank">Teaching Lab Studio</a>, where he investigates ways to better support educators and accelerate learners.</p>
		  <SocialSharing />
		  </article>
		</section>
	  {/if}
	</div>
	{#if $currentStage === stages.MAIN}
	<div class="controls-container">
        <span class="controls-label"><b>essay controls</b></span>
        <div class="controls">
            <button 
				on:click={handleRaiseHand} 
				class="raise-hand"
				disabled={isHandRaised}
				style="opacity: {isHandRaised ? '0.5' : '1'}">
				<span class="tooltip">✋</span>
			</button>
            <button 
			on:click={() => isPlaying ? pauseEssay() : resumeEssay()} 
			class="play-pause"
			>
			{isPlaying ? '⏸️' : '▶️'}
			</button>
        </div>
    </div>
	{/if}
	{#if showWavingHand}
		<WavingHand />
	{/if}
  
	{#if isQuestionModalOpen}
	<QuestionModal
		{isRecording}
		bind:question
		isMobile={isMobile}
		on:recordToggle={() => isRecording ? null : startRecording()}
		on:submit={() => isRecording ? stopRecording() : handleQuestionSubmit()}
		on:cancel={cancelQuestion}
	/>
	{/if}

	{#if aiAudioElement && !aiAudioElement.paused && playingAIAnswer}
        <AnswerPlayer aiAudioElement={aiAudioElement} />
    {/if}
  
	{#if errorMessage}
	  <div class="error">{errorMessage}</div>
	{/if}
  
	<audio bind:this={audioElement}
	crossorigin="anonymous"  />
	<audio bind:this={bufferAudioElement} 
	crossorigin="anonymous" />
	<audio bind:this={aiAudioElement}
    crossorigin="anonymous" />
</main>
	
<style>
	.loading-overlay {
		position: fixed;
		top: 0;
		left: 0;
		right: 0;
		bottom: 0;
		background: rgba(255, 255, 255, 0.9);
		display: flex;
		justify-content: center;
		align-items: center;
		z-index: 1000;
	}
	
	.loading-content {
		text-align: center;
	}
	
	.progress-bar {
		width: 300px;
		height: 20px;
		background: #eee;
		border-radius: 10px;
		overflow: hidden;
		margin: 20px 0;
	}
	
	.progress {
		height: 100%;
		background: #4CAF50;
		transition: width 0.3s ease;
	}
	.content-container {
		scroll-snap-type: y mandatory;
		width: 100%;
		max-width: 100vw;
		overflow-x: hidden;  /* Prevent horizontal scroll */
	}

	.title-view {
		height: 100vh;
		display: flex;
		flex-direction: column;
		justify-content: center;
		align-items: center;
		background-color: #ebd89eff;
		scroll-snap-align: start;
		font-family: EB Garamond, serif;
	}

	.title-view h1 {
		font-size: 8vw;
		text-align: center;
		max-width: 80%;
		margin-bottom: 0;
		line-height: 1.2;
	}

	.intro-view {
		height: 100vh;
		display: flex;
		flex-direction: column;
		justify-content: center;
		align-items: center;
		background-color: #cdf0e4ff;
		scroll-snap-align: start;
		font-family: EB Garamond, serif;
	}

	.intro-content {
		font-size: calc(8vw * 0.25); /* h2 relative to title */
		text-align: center;
		max-width: 80%;
		margin: 0 auto;
		line-height: 1.4;
	}

	.hidden {
		display: none;
	}

	.main-content {
		padding-top: 60px;
		width: 100%;
		max-width: 100%;
		box-sizing: border-box;
	}

	.menu-bar {
		position: fixed;
		top: 0;
		left: 0;
		right: 0;
		display: grid;
		grid-template-columns: 1fr auto 1fr; /* Three columns: left, center, right */
		align-items: center;
		padding: 1rem 2rem;
		background-color: #cdf0e4ff;
		z-index: 1002;
		transition: all 0.3s ease;
		height: 60px;
		width: 100%;
		box-sizing: border-box;
	}

	.menu-bar.visible {
		box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
	}

	.menu-left {
		display: flex;
		gap: 20px;
		align-items: center;
		justify-content: flex-start;
	}

	.menu-center {
		text-align: center;
	}

	.menu-right {
		display: flex;
		justify-content: flex-end;
	}

	.menu-bar-title {
		font-family: EB Garamond, serif;
		font-size: 1.2rem;
		margin: 0;
		opacity: 0;
		transition: opacity 0.3s ease;
		white-space: nowrap;
	}

	.menu-button {
		background: none;
		border: none;
		padding: 0.5rem 1rem;
		cursor: pointer;
		font-size: 1rem;
		color: inherit;
		text-decoration: none;
	}

	.start-button, .skip-button {
		display: flex;
		flex-direction: column;
		align-items: center;
		background: none;
		border: none;
		cursor: pointer;
		font-size: 1rem;
		margin-top: 20px;
	}
  
	#info {
		  color:#7c7c7c;
		  font-weight: 100;
		  font-style: italic;
	  }
  
	  .segment-container {
		position: relative;
		margin-bottom: 20px;
		transition: background-color 0.3s ease, color 0.3s ease;
		width: 100%;        /* Take full width of parent */
		box-sizing: border-box;
	}
  
	.segment-container.active {
		  background-color: #e6ffe6;
	  }
  
	.segment-container.played {
		  color: #888;
	  }
  
	  .comment-tooltip {
		position: absolute;
		right: -40px;
		top: 0;
		cursor: pointer;
		opacity: 0;
		transition: opacity 0.3s ease;
		width: 30px;  /* Give explicit width */
	}
  
	.segment-container:hover .comment-tooltip {
		  opacity: 1;
	  }
  
	.comment-box{
		  position: absolute;
		  right: -320px;
		  top: 0;
		  width: 300px;
		  background-color: #f9f9f9;
		  border: 1px solid #ddd;
		  border-radius: 4px;
		  padding: 10px;
		  box-shadow: 0 2px 5px rgba(0,0,0,0.1);
		  z-index: 1000;
	  }
  
	.comment-box textarea {
		  width: 100%;
		  height: 100px;
		  margin-bottom: 10px;
	  }
  
	  .controls-container {
        position: fixed;
        bottom: 30px;
        right: 30px;
        display: flex;
        flex-direction: column;
        align-items: center;
        gap: 8px;
		z-index: 1001; 
    }

    .controls-label {
        color: rgba(51, 51, 51, 0.8);
        font-size: 0.8rem;
        text-transform: lowercase;
    }

    .controls {
        /* Remove the fixed positioning since it's now in the container */
        position: static;
        background-color: rgba(51, 51, 51, 0.8);
        border-radius: 30px;
        padding: 10px 20px;
        display: flex;
        gap: 15px;
		box-shadow: 0 2px 5px rgba(3, 3, 3, 0.297);
    }
  
	.controls button {
		  background: none;
		  border: none;
		  color: white;
		  font-size: 24px;
		  cursor: pointer;
	  }
  
	@keyframes wave {
		  0% { transform: translateX(-50%) rotate(0deg); }
		  25% { transform: translateX(-50%) rotate(20deg); }
		  50% { transform: translateX(-50%) rotate(0deg); }
		  75% { transform: translateX(-50%) rotate(-20deg); }
		  100% { transform: translateX(-50%) rotate(0deg); }
	  }
  
	.submitted-comment {
		position: absolute;
		right: -320px;
		top: 0;
		width: 300px;
		background-color: #f9f9f9;
		border: 1px solid #ddd;
		border-radius: 4px;
		padding: 10px;
		padding-left: 15px;
		box-shadow: 0 2px 5px rgba(0,0,0,0.1);
		z-index: 1000;
		margin-top: 0; /* Remove the margin that was creating the gap */
		cursor: pointer;
		overflow: hidden; /* For clean collapse animation */
		transition: all 0.3s ease;
	}

	.submitted-comment.collapsed {
		height: 40px; /* Height of just the header/icon */
		padding: 5px 15px;
	}

	.comment-content {
		transition: opacity 0.3s ease;
		opacity: 1;
	}

	.collapsed .comment-content {
		opacity: 0;
		height: 0;
	}

	/* Style for the collapse indicator */
	.comment-header {
		display: flex;
		align-items: center;
		gap: 8px;
		margin-bottom: 8px;
	}

	.comment-header .collapse-icon {
		font-size: 1.2em;
		transition: transform 0.3s ease;
	}

	.collapsed .collapse-icon {
		transform: rotate(-90deg);
	}

	.user-comment, .ai-reply {
		display: flex;
		gap: 8px;
		align-items: start;
	}

	.ai-reply {
		margin-left: 16px;
		margin-top: 8px;
		padding-top: 8px;
		border-top: 1px solid #eee;
	}

	.comment-avatar, .reply-avatar {
		width: 24px;
		height: 24px;
		display: flex;
		align-items: center;
		justify-content: center;
		border-radius: 50%;
	}

	.comment-avatar {
		background-color: #e3f2fd;
	}

	.reply-avatar {
		background-color: #f3e5f5;
	}

	:global(body) {
		margin: 0;
		padding: 0;
		overflow-x: hidden;
	}

	.essay-content {
		font-family: EB Garamond, serif;
		font-size: 1.5rem;
		max-width: 800px;
		margin: 0 auto;
		padding: 40px 20px;
		position: relative;  /* Ensure it contains absolute positioned children */
		width: 100%;        /* Take full width of container */
		box-sizing: border-box; /* Include padding in width calculation */
	}
	.essay-content h1 {
		font-size: 4rem;
	}
	/* Add new styles */
	.floating-controls-container {
		position: fixed;
		bottom: 30px;
		left: 30px;
		display: flex;
		flex-direction: column;
		align-items: center;
		gap: 8px;
		z-index: 1001;
		opacity: 0.3; /* Start with low opacity */
		transition: opacity 0.3s ease; /* Smooth transition */
	}

	.floating-controls-container:hover {
		opacity: 1; /* Full opacity on hover */
	}

	.floating-controls-label {
		color: rgba(51, 51, 51, 0.8);
		font-size: 0.8rem;
		text-transform: lowercase;
	}

	.floating-ai-toggle {
		background-color: rgba(51, 51, 51, 0.8);
		border-radius: 30px;
		padding: 10px 20px;
		display: flex;
		gap: 15px;
		box-shadow: 0 2px 5px rgba(3, 3, 3, 0.297);
	}

	.floating-toggle-option {
		padding: 4px 12px;
		border-radius: 16px;
		transition: all 0.3s ease;
		font-size: 0.9rem;
		color: white;
		cursor: pointer;
	}

	.floating-toggle-option.active {
		background: white;
		color: black;
		box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
	}

	@media (max-width: 768px) {
		.menu-bar {
			grid-template-columns: auto auto;
			padding: 0.75rem;
			height: 50px;
			justify-content: space-between;
		}

		.menu-button {
			padding: 0.5rem;
			font-size: 0.9rem;
		}

		.main-content {
			padding-top: 50px; /* Match menu bar height */
		}
	}
	.controls button:disabled {
		cursor: not-allowed;
	}
	.gif-container {
	width: 100%;
	margin: 2rem 0;
	margin-top: 0%;
	margin-bottom: 0%;
	display: flex;
	justify-content: center;
	}

	.essay-gif {
	width: 100%;
	height: auto;
	max-width: 800px;
	}

	.floating-toggle-option {
	position: relative;
	}

	.floating-toggle-option::before {
	content: attr(data-tooltip);
	position: absolute;
	bottom: 100%;
	left: 50%;
	transform: translateX(-50%);
	padding: 4px 8px;
	background-color: rgba(0, 0, 0, 0.8);
	color: white;
	border-radius: 4px;
	font-size: 0.8rem;
	white-space: nowrap;
	opacity: 0;
	visibility: hidden;
	transition: opacity 0.2s, visibility 0.2s;
	}

	.floating-toggle-option:hover::before {
	opacity: 1;
	visibility: visible;
	}
  </style>